import CloseButtonModal from '../../../ui/close-button-modal';
import { useDispatch } from 'react-redux';
import { setAmendHistoryId, setAmendmentModal, setProposalIdAmendment, setSubAmendmentModal } from '../../../../features/actuarial/request-amendment/request-amendment.slice';
import { useDynamicQuires } from '../../../../hooks/services/dynamic-queries/use-dynamic-quires';
import { dateTimeFormatUTCDate } from '../../../../helper/date-time-formater';
import { useAmendmentsSocket } from '../../../../hooks/socket/use-amendments-socket';
import { AmendmentRequestHistoryData } from '../../../../api/request-amendment/request-amendment-api-state';
import { ProposalData } from '../../../../api/gms/proposal/proposal-api-state';
import { useToastify } from '../../../../library/toastify/toast-provider';
import LoadingState from '../../../ui/loadibg-state';
import ErrorState from '../../../ui/error-state';
import { useModalAccess } from '../../../global/modal/use-modal-access';

const getAmendmentStatusStyle = (status: string) => {
    switch (status?.toUpperCase()) {
        case 'PENDING':
            return {
                badge: 'border-amber-200 bg-amber-50 text-amber-700',
                response: 'border-amber-200 bg-amber-50/60',
                responseHeader: 'border-amber-200',
                responseIcon: 'border border-amber-200 bg-amber-100 text-amber-700',
                responseTitle: 'text-amber-700',
            };

        case 'APPROVED':
            return {
                badge: 'border-green-200 bg-green-50 text-green-700',
                response: 'border-green-200 bg-green-50/60',
                responseHeader: 'border-green-200',
                responseIcon: 'border border-green-200 bg-green-100 text-green-700',
                responseTitle: 'text-green-700',
            };

        case 'DECLINED':
        case 'REJECTED':
            return {
                badge: 'border-red-200 bg-red-50 text-red-700',
                response: 'border-red-200 bg-red-50/60',
                responseHeader: 'border-red-200',
                responseIcon: 'border border-red-200 bg-red-100 text-red-700',
                responseTitle: 'text-red-700',
            };

        default:
            return {
                badge: 'border-gray-200 bg-gray-50 text-gray-600',
                response: 'border-gray-200 bg-gray-50/60',
                responseHeader: 'border-gray-200',
                responseIcon: 'border border-gray-200 bg-gray-100 text-gray-600',
                responseTitle: 'text-gray-600',
            };
    }
};

interface Props {
    onViewProposed?: (amendment: any) => void;
}

export default function AmendmentHistoryLogs({ onViewProposed, }: Props) {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const { currentProposalId } = useModalAccess();

    const {
        getAmendmentHistoryLogs,
        isLoadingGetAmendmentHistoryLogs,
        isErrorGetAmendmentHistoryLogs,
    } = useDynamicQuires({
        proposalIdAmendment: currentProposalId,
    });
    
    const amendmentHistoryLogs = getAmendmentHistoryLogs ?? [];
    
    useAmendmentsSocket();

    const handleViewProposed = (amendment: null | ProposalData, amendId: number, proposalId: number) => {
        if (!amendment) {
            showToastNotify('No proposed amendment data available.', 'warning');
            return;
        }
        dispatch(setSubAmendmentModal('viewProposedDataInHistory'))
        dispatch(setProposalIdAmendment(proposalId))
        dispatch(setAmendHistoryId(amendId))
        // setProposedAmendment(amendment)
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4">
            <div className="flex h-[96dvh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:h-[90dvh] sm:max-w-[95vw] lg:h-[90dvh] lg:max-w-[700px] xl:max-w-[800px] 2xl:max-w-[1000px]">
                {/* HEADER */}
                <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#186cb2]/20 bg-[#186cb2]/10 text-[#186cb2]">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                            <path d="M3 12a9 9 0 1 0 3-6.7" />
                            <path d="M3 4v6h6" />
                            <path d="M12 7v5l3 2" />
                        </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold text-gray-900">
                            Amendment History
                        </h2>
                        <p className="mt-0.5 truncate text-xs text-gray-500">
                            Review all amendment requests and responses for this proposal.
                        </p>
                    </div>
                    <CloseButtonModal handleClose={() => dispatch(setAmendmentModal(null))} />
                </div>

                {/* CONTENT */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-[#f5f9fd] p-3 sm:p-4">
                    {/* LOADING */}
                    {isLoadingGetAmendmentHistoryLogs && (
                        <LoadingState />
                    )}
                    {/* ERROR */}
                    {!isLoadingGetAmendmentHistoryLogs && isErrorGetAmendmentHistoryLogs && (
                        <ErrorState/>
                    )}

                    {/* EMPTY */}
                    {!isLoadingGetAmendmentHistoryLogs && !isErrorGetAmendmentHistoryLogs && amendmentHistoryLogs.length === 0 && (
                        <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 text-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#186cb2]/20 bg-[#186cb2]/10 text-[#186cb2]">
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                    <path d="M6 4h12v16H6z" />
                                    <path d="M9 9h6" />
                                    <path d="M9 13h6" />
                                </svg>
                            </div>
                            <p className="text-xs font-medium text-gray-600">
                                No amendment history found.
                            </p>
                        </div>
                    )}

                    {/* HISTORY */}
                    {!isLoadingGetAmendmentHistoryLogs && !isErrorGetAmendmentHistoryLogs && amendmentHistoryLogs.length > 0 && (
                            <div className="space-y-3">
                                {amendmentHistoryLogs.map((req: AmendmentRequestHistoryData, index: number) => {
                                    const statusStyle = getAmendmentStatusStyle(req.amendment_status);
                                    const status = req.amendment_status?.toUpperCase();
                                    const isPending = status === 'PENDING';
                                    const isApproved = status === 'APPROVED';

                                    return (
                                        <div key={req.amendment_id} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                            {/* AMENDMENT HEADER */}
                                            <div className="flex min-h-11 items-center gap-2 border-b border-gray-200 bg-white px-3 sm:px-4">
                                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[#186cb2]/20 bg-[#186cb2]/10 text-[#186cb2]">
                                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                        <path d="M5 12h14" />
                                                        <path d="m13 6 6 6-6 6" />
                                                    </svg>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                                                            Amendment # {amendmentHistoryLogs.length - index}
                                                        </span>
                                                        {index === 0 && (
                                                            <span className="rounded-full border border-[#186cb2]/20 bg-[#186cb2]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#186cb2]">
                                                                Latest
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`shrink-0 rounded-md border px-2 py-0.5 text-[9px] font-semibold uppercase ${statusStyle.badge}`}
                                                >
                                                    {req.amendment_status}
                                                </span>
                                            </div>
                                            {/* REQUEST / RESPONSE */}
                                            <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-stretch sm:p-4">
                                                {/* REQUEST */}
                                                <div className="rounded-md border border-[#186cb2]/20 bg-[#186cb2]/5 p-3">
                                                    {/* REQUEST HEADER */}
                                                    <div className="flex min-h-8 items-center gap-2 border-b border-[#186cb2]/20 pb-2">
                                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#186cb2]/20 bg-white text-[#186cb2]">
                                                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                <circle cx="12" cy="8" r="3" />
                                                                <path d="M5 21a7 7 0 0 1 14 0" />
                                                            </svg>
                                                        </div>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#186cb2]">
                                                            Request
                                                        </span>
                                                    </div>
                                                    {/* REQUEST CONTENT */}
                                                    <div className="pt-2.5">
                                                        <p className="text-xs font-semibold text-gray-800">
                                                            {req.requested_by_name || '—'}
                                                        </p>
                                                        <p className="mt-0.5 text-[10px] text-gray-500">
                                                            {req.request_dept_name ||
                                                                '—'}
                                                        </p>
                                                        <div className="mt-2 rounded-md border border-[#186cb2]/10 bg-white p-2">
                                                            <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-700">
                                                                {req.request_notes || (
                                                                    <span className="italic text-gray-400">
                                                                        No request notes.
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                                            <div className="flex items-center gap-1 text-[9px] text-gray-400">
                                                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                    <circle cx="12" cy="12" r="9" />
                                                                    <path d="M12 7v5l3 2" />
                                                                </svg>
                                                                {dateTimeFormatUTCDate(req.requested_at)}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewProposed(req.proposed_data, req.amendment_id, req.application_id)}
                                                                disabled={!req.proposed_data}
                                                                className="inline-flex items-center gap-1.5 rounded-md border border-[#186cb2]/20 bg-white px-2.5 py-1 text-[9px] font-semibold text-[#186cb2] transition hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                                                            >
                                                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                                                                    <circle cx="12" cy="12" r="2.5" />
                                                                </svg>
                                                                View Proposed
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* CONNECTOR */}
                                                <div className="hidden items-center justify-center sm:flex">
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-full border border-[#186cb2]/20 bg-white text-[#186cb2]">
                                                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                            <path d="M5 12h14" />
                                                            <path d="m13 6 6 6-6 6" />
                                                        </svg>
                                                    </div>
                                                </div>
                                                {/* RESPONSE */}
                                                <div className={`rounded-md border p-3 transition-colors ${statusStyle.response}`}>
                                                    {/* RESPONSE HEADER */}
                                                    <div className={`flex min-h-8 items-center gap-2 border-b pb-2 ${statusStyle.responseHeader}`}>
                                                        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${statusStyle.responseIcon}`}>
                                                            {isPending ? (
                                                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                    <circle cx="12" cy="12" r="9" />
                                                                    <path d="M12 7v5l3 2" />
                                                                </svg>
                                                            ) : isApproved ? (
                                                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                    <path d="m5 12 4 4L19 6" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                    <path d="M6 6l12 12" />
                                                                    <path d="M18 6 6 18" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${statusStyle.responseTitle}`}>
                                                            Response
                                                        </span>
                                                    </div>
                                                    {/* RESPONSE CONTENT */}
                                                    <div className="pt-2.5">
                                                        {isPending ? (
                                                            <>
                                                                <p className="text-xs font-semibold text-amber-800">
                                                                    Awaiting response
                                                                </p>
                                                                <p className="mt-1 text-[11px] italic text-amber-700/70">
                                                                    Waiting for Actuarial review...
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-xs font-semibold text-gray-800">
                                                                    {req.responded_by_name || '—'}
                                                                </p>
                                                                <p className="mt-0.5 text-[10px] text-gray-500">
                                                                    {req.target_dept_name ||  '—'}
                                                                </p>
                                                                <div className="mt-2 rounded-md border border-white/70 bg-white p-2">
                                                                    {isApproved ? (
                                                                        <p className="text-[11px] leading-relaxed text-green-700">
                                                                            Amendment request approved.
                                                                        </p>
                                                                    ) : (
                                                                        <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-700">
                                                                            {req.response_notes || (
                                                                                <span className="italic text-gray-400">
                                                                                    No response notes.
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="mt-2 flex items-center gap-1 text-[9px] text-gray-400">
                                                                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                                        <circle cx="12" cy="12" r="9" />
                                                                        <path d="M12 7v5l3 2" />
                                                                    </svg>
                                                                    {dateTimeFormatUTCDate(req.responded_at)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
}